import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Gift, Copy, Sparkles, X, ArrowUpRight } from "lucide-react";

// Referral programme surface. Invitation, never a gate — shown after a successful deploy and as a
// persistent DISMISSIBLE dashboard spot. The standard upgrade-to-Pro option is presented with equal
// visual weight, never made to look like the slower path. No fake urgency; no fabricated social proof.
export function ReferralCard({ context = "dashboard", appName = "", appDescription = "", onUpgrade }) {
  const [status, setStatus] = useState(null);
  const [activity, setActivity] = useState(null);
  const [code, setCode] = useState("");
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem("kadana_referral_dismissed") === "1"; } catch { return false; }
  });
  const [copy, setCopy] = useState("");
  const [channel, setChannel] = useState("linkedin");

  useEffect(() => {
    api.get("/referral/status").then((r) => setStatus(r.data)).catch(() => {});
    api.get("/referral/activity").then((r) => setActivity(r.data)).catch(() => {});
  }, []);

  if (dismissed && context === "dashboard") return null;

  const makeInvite = async () => {
    try {
      const r = await api.post("/referral/invite", {});
      setCode(r.data.code);
      const link = `${window.location.origin}${r.data.share_path}`;
      navigator.clipboard.writeText(link).then(() => toast.success("Invite link copied")).catch(() => {});
    } catch (e) { toast.error(errMsg(e)); }
  };
  const genCopy = async () => {
    try {
      const r = await api.post("/referral/share-copy", {
        channel, app_name: appName, app_description: appDescription, referral_code: code,
      });
      setCopy(r.data.copy || "");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem("kadana_referral_dismissed", "1"); } catch { /* ignore */ }
  };

  const headline = context === "deploy"
    ? "Your app is live 🎉 Invite a fellow builder"
    : "Grow with Kadana — invite a fellow builder";

  return (
    <div className="k-container" data-testid="referral-card" style={{ padding: 18, position: "relative" }}>
      {context === "dashboard" ? (
        <button className="k-btn k-btn-ghost" data-testid="referral-dismiss" onClick={dismiss}
          aria-label="Dismiss" style={{ position: "absolute", top: 10, right: 10, padding: "3px 6px" }}>
          <X size={13} />
        </button>
      ) : null}

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <Gift size={18} color="var(--accent)" />
        <h3 className="k-heading" style={{ fontSize: 16, margin: 0 }}>{headline}</h3>
      </div>
      <p className="k-muted" style={{ fontSize: 13, marginTop: 0 }}>
        You'll both get a free month of Pro when they ship their first project. No catch, no countdown —
        the reward applies once your invitee completes their first real deploy.
      </p>

      {status ? (
        <div data-testid="referral-progress" style={{ display: "flex", gap: 14, flexWrap: "wrap", margin: "8px 0" }}>
          <span className="k-badge k-badge-accent"><Sparkles size={12} /> {status.converted_all_time} converted</span>
          <span className="k-muted" style={{ fontSize: 12 }}>{status.converted_rolling_30d} in the last 30 days · {status.pending} pending</span>
          {status.pro_until ? <span className="k-muted" style={{ fontSize: 12 }}>Pro until {String(status.pro_until).slice(0, 10)} (max {status.pro_months_cap} months)</span> : null}
        </div>
      ) : null}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
        <button className="k-btn k-btn-primary" data-testid="referral-invite" onClick={makeInvite}>
          <Copy size={14} /> {code ? "Copy invite link again" : "Create & copy my invite link"}
        </button>
        <button className="k-btn k-btn-ghost" data-testid="referral-upgrade" onClick={onUpgrade}>
          <ArrowUpRight size={14} /> Upgrade to Pro directly
        </button>
      </div>
      <p className="k-muted" style={{ fontSize: 11, marginTop: 6 }}>
        Prefer not to wait? Upgrading to Pro is available any time at standard pricing — an equal, immediate option.
      </p>

      {code ? (
        <div data-testid="referral-sharecopy" style={{ marginTop: 12, borderTop: "1px solid var(--border)", paddingTop: 12 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span className="k-muted" style={{ fontSize: 12 }}>Draft honest share copy:</span>
            <select className="k-control" data-testid="referral-channel" value={channel} onChange={(e) => setChannel(e.target.value)} style={{ width: "auto", fontSize: 12, padding: "4px 6px" }}>
              <option value="linkedin">LinkedIn</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="email">Email</option>
              <option value="x">X</option>
            </select>
            <button className="k-btn k-btn-ghost" data-testid="referral-gencopy" onClick={genCopy} style={{ fontSize: 12, padding: "4px 10px" }}>Generate</button>
          </div>
          {copy ? <textarea className="k-control" data-testid="referral-copy-text" readOnly value={copy} rows={3} style={{ fontSize: 13, marginTop: 8 }} /> : null}
        </div>
      ) : null}

      {activity && activity.real_conversions_last_24h > 0 ? (
        <p className="k-muted" data-testid="referral-activity" style={{ fontSize: 11, marginTop: 10 }}>
          {activity.message}
        </p>
      ) : null}
    </div>
  );
}
